import { Registry, type Identified } from './Registry';
import type { ToolClass } from './items';

/**
 * Toplanabilir dünya kaynakları (ağaç, kaya, çalı, maden damarı, bitki).
 * Vuruş sayısı, gereken alet, düşen eşya ve yenilenme süresi veriden gelir.
 */

export type ResourceKind = 'tree' | 'rock' | 'bush' | 'ore' | 'plant' | 'sand';

export interface DropSpec {
  item: string;
  min: number;
  max: number;
  /** 0..1 düşme şansı. */
  chance: number;
}

export interface ResourceDef extends Identified {
  name: string;
  kind: ResourceKind;
  /** Toplam dayanıklılık; her vuruş alet gücüne göre düşürür. */
  health: number;
  /** Bu sertliği karşılayan alet gerekir (0 = elle toplanır). */
  hardness: number;
  /** Verimli alet sınıfı (yanlış aletle çok yavaş). */
  tool: ToolClass | null;
  drops: DropSpec[];
  /** Saniye cinsinden yenilenme; 0 = yenilenmez. */
  respawn: number;
  /** Görsel ölçek ve renk (mesh üretimi için). */
  size: number;
  color: number;
  /** Çarpışma yarıçapı. */
  radius: number;
}

export const resourceDefs = new Registry<ResourceDef>('resources');

resourceDefs.register(
  {
    id: 'tree_oak',
    name: 'Meşe',
    kind: 'tree',
    health: 60,
    hardness: 0,
    tool: 'axe',
    drops: [
      { item: 'wood', min: 3, max: 6, chance: 1 },
      { item: 'stick', min: 1, max: 3, chance: 0.6 },
      { item: 'apple', min: 1, max: 1, chance: 0.18 },
    ],
    respawn: 900,
    size: 1,
    color: 0x4e342e,
    radius: 1.1,
  },
  {
    id: 'tree_pine',
    name: 'Çam',
    kind: 'tree',
    health: 70,
    hardness: 0,
    tool: 'axe',
    drops: [
      { item: 'wood', min: 4, max: 7, chance: 1 },
      { item: 'fiber', min: 1, max: 2, chance: 0.4 },
    ],
    respawn: 900,
    size: 1.15,
    color: 0x3b2d21,
    radius: 1.1,
  },
  {
    id: 'tree_venom',
    name: 'Zehirli Ağaç',
    kind: 'tree',
    health: 85,
    hardness: 1,
    tool: 'axe',
    drops: [
      { item: 'wood', min: 3, max: 6, chance: 1 },
      { item: 'venom_sac', min: 1, max: 2, chance: 0.35 },
    ],
    respawn: 1200,
    size: 1.1,
    color: 0x4a2a63,
    radius: 1.1,
  },
  {
    id: 'tree_frost',
    name: 'Kar Ağacı',
    kind: 'tree',
    health: 75,
    hardness: 0,
    tool: 'axe',
    drops: [{ item: 'wood', min: 3, max: 6, chance: 1 }],
    respawn: 1200,
    size: 1,
    color: 0x5c6b74,
    radius: 1.1,
  },
  {
    id: 'rock_boulder',
    name: 'Kaya',
    kind: 'rock',
    health: 90,
    hardness: 1,
    tool: 'pickaxe',
    drops: [
      { item: 'stone', min: 3, max: 7, chance: 1 },
      { item: 'coal', min: 1, max: 2, chance: 0.15 },
    ],
    respawn: 1200,
    size: 1,
    color: 0x8b8b8b,
    radius: 1.3,
  },
  {
    id: 'bush_berry',
    name: 'Böğürtlen Çalısı',
    kind: 'bush',
    health: 12,
    hardness: 0,
    tool: null,
    drops: [
      { item: 'berry', min: 2, max: 4, chance: 1 },
      { item: 'berry_seed', min: 1, max: 1, chance: 0.35 },
      { item: 'fiber', min: 1, max: 2, chance: 0.5 },
    ],
    respawn: 420,
    size: 0.6,
    color: 0x3f6b32,
    radius: 0.6,
  },
  {
    id: 'plant_fiber',
    name: 'Lifli Bitki',
    kind: 'plant',
    health: 8,
    hardness: 0,
    tool: null,
    drops: [
      { item: 'fiber', min: 2, max: 4, chance: 1 },
      { item: 'wheat_seed', min: 1, max: 1, chance: 0.2 },
    ],
    respawn: 300,
    size: 0.5,
    color: 0x7a8f45,
    radius: 0.4,
  },
  {
    id: 'plant_glow',
    name: 'Parlayan Mantar',
    kind: 'plant',
    health: 8,
    hardness: 0,
    tool: null,
    drops: [{ item: 'glow_mushroom', min: 1, max: 3, chance: 1 }],
    respawn: 600,
    size: 0.5,
    color: 0xc9a0ff,
    radius: 0.4,
  },
  {
    id: 'sand_pile',
    name: 'Kum Yığını',
    kind: 'sand',
    health: 20,
    hardness: 0,
    tool: 'shovel',
    drops: [{ item: 'sand', min: 3, max: 6, chance: 1 }],
    respawn: 300,
    size: 0.7,
    color: 0xe6d6a2,
    radius: 0.7,
  },
);

/** Maden damarları malzeme tanımından türetilir (materials.vein). */
export function oreResourceId(materialId: string): string {
  return `ore_${materialId}`;
}
